import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, ListItem } from '@material-ui/core';
import {
  ChapterAccordion,
  ChapterAccordionSummary,
  ChapterList,
} from 'components';
import { Button } from '@redshelf/redshelf-ui';
import { useLimitedItems } from './useLimitedItems';

const useStyles = makeStyles((theme) => ({
  root: {},
  listItem: {
    ...theme.typography.body1,
    alignItems: 'stretch',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    cursor: ({ isNavItem }) => (isNavItem ? 'pointer' : 'default'),
    flexDirection: 'column',
    justifyContent: 'center',
    padding: 0,
  },
  listItemContainer: {
    display: 'flex',
    minHeight: theme.spacing(7),
  },
  loadMoreButton: {
    margin: `${theme.spacing(2)}px auto`,
  },
  accordionSummary: {
    '&.MuiAccordionSummary-root': {
      minHeight: theme.spacing(7),
      '&.Mui-expanded': {
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
      },
    },

    '&.MuiAccordionSummary-content': {
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      '&:last-child': {
        borderBottom: 'none',
      },
      margin: 0,
    },

    '&.MuiAccordionSummary-expandIconWrapper': {
      margin: theme.spacing(5),
    },
  },
}));

const ChapterListItem = ({
  children,
  defaultExpanded = null,
  id,
  isNavItem = true,
  orientation = 'right',
  shouldHaveLoadMoreButton = false,
  title,
  ariaLabelChapterTitle,
  listItemId,
  ...props
}) => {
  const classes = useStyles({ isNavItem });

  const { limitedItems, handleLoadMore, hasMore } = useLimitedItems(children);

  return (
    <ListItem
      id={listItemId}
      className={classes.listItem}
      disableGutters
      {...props}
    >
      {children ? (
        <ChapterAccordion
          defaultExpanded={defaultExpanded}
          orientation={orientation}
        >
          <ChapterAccordionSummary
            aria-controls={`content-${id}`}
            id={id}
            isNavItem={isNavItem}
            ariaLabelChapterTitle={ariaLabelChapterTitle}
          >
            {title}
          </ChapterAccordionSummary>
          <ChapterList>
            {shouldHaveLoadMoreButton ? limitedItems : children}
            {shouldHaveLoadMoreButton && hasMore ? (
              <Button
                className={classes.loadMoreButton}
                onClick={handleLoadMore}
              >
                Load More
              </Button>
            ) : null}
          </ChapterList>
        </ChapterAccordion>
      ) : (
        <div className={classes.listItemContainer}>{title}</div>
      )}
    </ListItem>
  );
};

ChapterListItem.propTypes = {
  /**
   * If there are children the component will render as an accordion.
   */
  children: PropTypes.node,
  /**
   * Option for this element to expand the accordion by default.
   */
  defaultExpanded: PropTypes.bool,
  /**
   * Associates aria-controls for accordion if there are children.
   */
  id: PropTypes.string,
  isNavItem: PropTypes.bool,
  /**
   * Id for the list item
   */
  listItemId: PropTypes.string,
  /**
   * Default is 'right' with 'left' as the other option.
   */
  orientation: PropTypes.string,
  /**
   * Should list item display a "Load More" button? Default 'false'
   */
  shouldHaveLoadMoreButton: PropTypes.bool,
  /**
   * The chapter title to display.
   */
  title: PropTypes.node.isRequired,
};

export default ChapterListItem;
